import {useAddGiftsToCart, useAvailableGiftsInCart} from '@/framework/gift';
import {useTrackingOngoingAddToCartServerSide} from '@/store/cart-mutation';
import {GiftsCartState, giftsCartReducer, initialState} from '@/store/gifts-cart/gifts-cart.reducer';
import {PickedGiftsPayload} from '@/store/gifts-cart/gifts-cart.utils';
import {cartStoreAPI} from '@kidslabs/module-quote';
import {useSelector} from '@legendapp/state/react';
import {AvailableGifts, CartItemInterface, PickedGiftInput, ProductInterface} from '__generated__/__types__';
import React, {createContext, useContext, useMemo, useReducer, useState, useRef, PropsWithChildren, useCallback, useEffect} from 'react';
import {isProductInStock} from '@/_application/modules/products/model/stock';

interface GiftsCartProviderState extends GiftsCartState {
    resetGiftsCart: () => void;
    setPickedGiftsPayload: (productId: number, pickedGiftsPayload: PickedGiftsPayload) => void;
    changeProductGiftInRule: (ruleId: number, productSku: string) => void;
    changeProductId: (productId: number) => void;
    addGiftToCartServerSide: (cartItemId: number, pickedGifts: PickedGiftInput[]) => void;
    fetchAvailableGifts: () => void;
}

export const GiftsCartContext = createContext<GiftsCartProviderState | undefined>(undefined);

GiftsCartContext.displayName = 'GiftsCartContext';

export const useGiftsCart = () => {
    const context = useContext(GiftsCartContext);
    if (context === undefined) {
        throw new Error(`useGiftsCart must be used within a GiftsCartProvider`);
    }
    return useMemo(() => context, [context]);
};

export const GiftsCartProvider = ({children, page}: PropsWithChildren<{page: 'detail' | 'cart'}>) => {
    const [state, dispatch] = useReducer(giftsCartReducer, initialState);
    const cartItems = useSelector(() => cartStoreAPI.state.getCartItemsObservable().get() ?? []);
    const cartId = useSelector(() => cartStoreAPI.state.getCartIdObservable().get() ?? '');
    const isFetchingCart = cartStoreAPI.loading.isFetchingCart();
    const [giftPayloadLoaded, setGiftPayloadLoaded] = useState(false);
    const {addGiftsToCartServerSideAsync} = useAddGiftsToCart();
    const {getAvailableGiftsInCart, availableGifts} = useAvailableGiftsInCart();
    const {ongoingAddToCartServerSideCount, resetOngoingCall} = useTrackingOngoingAddToCartServerSide();
    const abortController = useRef<AbortController | undefined>(undefined);

    const fetchAvailableGifts = useCallback(() => {
        if (!cartId) {
            return;
        }
        const controller = new window.AbortController();
        abortController.current = controller;
        setIsGettingAvailableGifts(true);

        getAvailableGiftsInCart(cartId);
        setIsGettingAvailableGifts(false);
    }, [cartId]);

    useEffect(() => {
        if (!availableGifts?.length) {
            setAvailableGifts([]);
            return;
        }

        const inStockGiftItems = availableGifts
            ?.filter(gift => gift)
            .map(gift => {
                const validProductGifts =
                    gift?.products?.filter(item => item && isProductInStock(item as ProductInterface)) ?? [];
                return {...gift, products: validProductGifts};
            }) as AvailableGifts[];

        setAvailableGifts(inStockGiftItems);
    }, [JSON.stringify(availableGifts)]);

    useEffect(() => {
        if (page !== 'cart') {
            return;
        }
        fetchAvailableGifts();
    }, [cartId]);

    // refetch available gifts in cart when last cart mutation request completed
    useEffect(() => {
        if (page !== 'cart') {
            return;
        }
        if (ongoingAddToCartServerSideCount !== -1 && state.isGettingAvailableGifts) {
            abortController.current && abortController.current.abort();
        }
        if (ongoingAddToCartServerSideCount === 0) {
            fetchAvailableGifts();
            resetOngoingCall();
        }
    }, [ongoingAddToCartServerSideCount]);

    // TODO: need to find another way to update gift after add to cart, current solution is too slow
    useEffect(() => {
        const payload = state.pickedGiftsPayload;
        if (
            isFetchingCart ||
            !state.productId ||
            !payload?.picked_gifts?.length ||
            !cartId ||
            !giftPayloadLoaded ||
            page !== 'detail'
        ) {
            return;
        }
        const cartItemNeedToUpdateGift = cartItems.find(item => item?.product?.id === state.productId);
        if (cartItemNeedToUpdateGift?.id && ongoingAddToCartServerSideCount === -1) {
            addGiftToCartServerSide(+cartItemNeedToUpdateGift.id, payload.picked_gifts);
        }
    }, [isFetchingCart]);

    const addGiftToCartServerSide = (cartItemId: number, pickedGifts: PickedGiftInput[]) => {
        if (!cartId && !cartItemId) {
            return;
        }
        setIsAddingGiftToCart(true);
        addGiftsToCartServerSideAsync(cartId, cartItemId, pickedGifts)
            .then(response => {
                if (response.data?.addGiftsToCart?.cart.items?.length) {
                    if (state.availableGiftsInCart?.length) {
                        removeAvailableGifts(cartItemId);
                    }
                    // Update cart store with new items from server response
                    cartStoreAPI.actions.setItems(
                        response.data.addGiftsToCart.cart.items as CartItemInterface[],
                        cartId
                    );
                }
            })
            .finally(() => {
                setIsAddingGiftToCart(false);
            });
    };

    const resetGiftsCart = () => dispatch({type: 'RESET_GIFT_CART_STATE'});

    const setPickedGiftsPayload = (productId: number, item: PickedGiftsPayload) => {
        setGiftPayloadLoaded(true);
        dispatch({type: 'SET_PICKED_GIFTS_PAYLOAD', productId, pickedGiftsPayload: item});
    };

    const changeProductGiftInRule = (ruleId: number, productSku: string) => {
        const payload = state.pickedGiftsPayload;
        if (!payload?.picked_gifts?.length) {
            return;
        }
        const pickedGiftItem = payload?.picked_gifts?.find(item => item?.rule_id === ruleId);
        if (pickedGiftItem?.sku) {
            pickedGiftItem.sku = productSku;
        }
        const updatedPayload = {...payload};
        dispatch({type: 'CHANGE_PICKED_GIFTS_PAYLOAD', pickedGiftsPayload: updatedPayload});
    };

    const changeProductId = (productId: number) => {
        dispatch({type: 'CHANGE_PRODUCT_ID', productId});
    };

    const setIsAddingGiftToCart = (isLoading = false) => {
        dispatch({type: 'SET_IS_ADDING_GIFT_TO_CART', isLoading});
    };

    const setAvailableGifts = (availableGifts: AvailableGifts[]) => {
        dispatch({type: 'SET_AVAILABLE_GIFTS', availableGifts});
    };

    const removeAvailableGifts = (cartItemId: number) => {
        dispatch({type: 'REMOVE_AVAILABLE_GIFTS', cartItemId});
    };

    const setIsGettingAvailableGifts = (isLoading: boolean) => {
        dispatch({type: 'SET_IS_GETTING_AVAILABLE_GIFTS', isLoading});
    };

    const value = useMemo(
        () => ({
            ...state,
            resetGiftsCart,
            setPickedGiftsPayload,
            changeProductGiftInRule,
            changeProductId,
            addGiftToCartServerSide,
            fetchAvailableGifts,
        }),
        [state],
    );
    return <GiftsCartContext.Provider value={value}>{children}</GiftsCartContext.Provider>;
};
